import { PlusAccordionSummary } from '@components/display/accordions';
import { WarningAlertBox } from '@components/display/alerts';
import { CheckToggleButton } from '@components/utility/buttons';
import { AllowTemporaryRegistrationDialog } from '@event/components/display/dialogs';
import { BadgeList } from '@event/components/display/tabs/Badges';
import { useEventDashboardContext } from '@event/contexts/EventContext';
import { EventFormikType } from '@event/types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Accordion,
    AccordionDetails,
    AccordionProps,
    Card,
    CardContent,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import clsx from 'clsx';
import { parseISO } from 'date-fns/parseISO';
import { useFormikContext } from 'formik';
import { useMemo, useState } from 'react';

const Badges = ({
    accordion = true,
    ...props
}: Omit<
    AccordionProps & {
        accordion?: boolean;
    },
    'children'
>) => {
    const [isTemporaryRegistrationDialogOpen, setIsTemporaryRegistrationDialogOpen] = useState(false);

    const { event, isEdit } = useEventDashboardContext();
    const { values, setFieldValue } = useFormikContext<EventFormikType>();

    const hasEventEnded = useMemo(
        () => (event?.date_end ? new Date() >= parseISO(event.date_end) : true),
        [event?.date_end]
    );

    const handleTemporaryRegistrationChange = (_e: React.MouseEvent<HTMLElement>, newValue: boolean) => {
        if (newValue !== null) {
            if (newValue) {
                setIsTemporaryRegistrationDialogOpen(true);
            } else {
                setFieldValue('allow_temporary_registration', false);
            }
        }
    };

    return (
        <Accordion className={clsx('mt-10 mb-2', props.className)} {...props}>
            <PlusAccordionSummary
                isExpanded={props.expanded}
                className={clsx({ 'pointer-events-none': !accordion })}
                {...(!accordion ? { expandIcon: null } : {})}
            >
                <Typography fontFamily="museo-sans" fontSize={24} letterSpacing={-1}>
                    Event Badges
                </Typography>
            </PlusAccordionSummary>
            <AccordionDetails>
                <Typography fontSize={16}>
                    Add required or rewarded badges, guests only need one required badge to access the event.
                </Typography>
                {hasEventEnded ? (
                    <WarningAlertBox className="mt-5">
                        <Typography fontWeight={500} fontSize={16} color="dark-yellow.main">
                            This is a past event. Only reward badges can be added. Saving will permanently assign them
                            to all attendees. This cannot be reversed.
                        </Typography>
                    </WarningAlertBox>
                ) : null}
                <BadgeList />
                <Card className="rounded-lg black-border mt-5">
                    <CardContent className="px-7 py-9">
                        <Typography fontSize={20} fontFamily="museo-sans" letterSpacing={-1} className="mb-3">
                            Allow Temporary Registrations Regardless Of Required Badges?
                        </Typography>
                        <ToggleButtonGroup
                            orientation="vertical"
                            value={!!values.allow_temporary_registration}
                            exclusive
                            onChange={handleTemporaryRegistrationChange}
                            disabled={!isEdit}
                            fullWidth
                        >
                            <CheckToggleButton value={false}>
                                <Typography
                                    className={clsx({
                                        'font-semibold': !isEdit && !values.allow_temporary_registration,
                                    })}
                                    fontFamily="Inter"
                                    variant="body1"
                                >
                                    Allow users to register only if they have the necessary badge.
                                </Typography>
                            </CheckToggleButton>
                            <CheckToggleButton value={true}>
                                <Typography
                                    className={clsx('flex items-center gap-1', {
                                        'font-semibold': !isEdit && values.allow_temporary_registration,
                                    })}
                                    fontFamily="Inter"
                                    variant="body1"
                                >
                                    Allow users to register if they are registered for a previous event that awards the
                                    required badge.
                                    <Tooltip
                                        title="By enabling temporary registration, attendees who are 
                already registered for prerequisite events (that award the required badges) 
                will be able to reserve a spot in this event before completing those prerequisites."
                                        arrow
                                        placement="right"
                                    >
                                        <span>
                                            <InfoOutlinedIcon className="text-spanish-gray" />
                                        </span>
                                    </Tooltip>
                                </Typography>
                            </CheckToggleButton>
                        </ToggleButtonGroup>
                    </CardContent>
                </Card>
                <AllowTemporaryRegistrationDialog
                    open={isTemporaryRegistrationDialogOpen}
                    onClose={() => setIsTemporaryRegistrationDialogOpen(false)}
                />
            </AccordionDetails>
        </Accordion>
    );
};

export { Badges };
